/**
 * capabilityManifest.ts — the browser's GENERIC Engine capability consumer.
 *
 * The Engine owns engineering semantics. This module therefore names NO
 * operation, NO query, NO parameter and NO enum. It reads whatever the Engine
 * publishes at `GET /api/v1/capabilities` and interprets it, so a compatible
 * new Engine capability becomes usable here without a semantic source edit.
 *
 * That property is the whole point, and it is testable: `validateManifest`
 * below is handed an UNSEEN fixture capability in a domain this repository has
 * never heard of and must accept it. A hand-maintained allowlist would reject
 * it; a vendor-per-descriptor table would need a new entry.
 *
 * THREE RULES, each of which fails CLOSED:
 *
 *  1. VERSION. A manifest whose contract is outside the version this build
 *     serves is REFUSED, never best-effort parsed. A client that silently
 *     half-understands a contract produces wrong pictures of real plants.
 *  2. DIGEST. A configured expected digest is checked. The digest pins
 *     content; the version pins shape. A manifest that changed under us at the
 *     same version is exactly the case a pin exists to catch.
 *  3. COMPLETENESS. A document missing a required section is REFUSED. A
 *     capability that fails to appear is the one failure mode discovery
 *     exists to prevent — reporting it as absent would be a lie.
 *
 * What is deliberately NOT here: any notion of a client allowlist, a
 * `readPath`/`previewPath` internal dispatch seam, or a hand-listed set of
 * known capability ids. Those are exactly the duplication this replaces.
 */

import { ApiError, TransportError } from "./errors";

/** The contract this build is able to interpret. */
export const SUPPORTED_CONTRACT = "plantlifecycle.capability-manifest/1";

/** Refusal codes. Deliberately distinct: version, shape and identity differ. */
export const MANIFEST_VERSION_UNSUPPORTED = "engine-capability-manifest-version-unsupported";
export const MANIFEST_MALFORMED = "engine-capability-manifest-malformed";
export const MANIFEST_DIGEST_MISMATCH = "engine-capability-manifest-digest-mismatch";
export const MANIFEST_INCOMPLETE = "engine-capability-manifest-incomplete";

/** Refusal thrown by this module. Distinct from ApiError (server-decided). */
export class CapabilityManifestError extends Error {
  readonly code: string;
  readonly status: number | undefined;
  constructor(code: string, message: string, status?: number) {
    super(message);
    this.name = "CapabilityManifestError";
    this.code = code;
    this.status = status;
  }
  /** An incompatible manifest is not recoverable by retrying. */
  get isTerminal(): boolean { return true; }
}

// -- the bounded shapes this module reads ------------------------------------
//
// These mirror the Engine's published contract structurally. They are a
// TRANSPORT interpretation, not a semantic copy: every field is either a
// closed vocabulary the Engine itself published or a bounded JSON-Schema
// fragment passed through untouched.

export type CapabilityKind = "operation" | "query" | "projection" | "extension";
export type CapabilityMode = "READ" | "PROPOSE" | "EXECUTE";
export type CapabilityRisk = "read-only" | "representation-write" | "canonical-write" | "consequential-write" | "reversible-draft";
/**
 * The Engine's own review vocabulary, read from
 * `REVIEW_POLICIES` in `src/application/capabilityContract.ts`.
 *
 * These four values are the ENGINE's, copied so this module can REFUSE a value
 * it does not understand rather than coerce it. Writing a plausible-sounding
 * set here instead — `human-approval` was the first attempt — is exactly the
 * duplication this consumer is meant to remove, and it is silent: a stricter
 * browser would reject the whole manifest and a looser one would invent
 * authority the Engine never granted. `tests/realEngineManifest.test.ts`
 * validates this list against the manifest the Engine actually builds, so a
 * vocabulary change on the Engine side fails here by name.
 */
export type CapabilityReview = "none" | "change-set" | "independent-approval" | "human-publication";

export interface JsonSchemaFragment {
  type?: string;
  description?: string;
  enum?: readonly unknown[];
  items?: JsonSchemaFragment;
  properties?: Record<string, JsonSchemaFragment>;
  required?: readonly string[];
  additionalProperties?: boolean | JsonSchemaFragment;
  [keyword: string]: unknown;
}

export interface CapabilityApplicability {
  kind: string;
  contextPermission: string;
  requires: readonly string[];
  canonicalInputs: readonly string[];
}

export interface CapabilityPresentation {
  group: string;
  summary: string;
  primaryFields: readonly string[];
  readOnlyFields: readonly string[];
}

export interface CapabilityDescriptor {
  id: string;
  /**
   * The exact value to send as the envelope address — REQUIRED, never derived.
   *
   * The Engine publishes this per descriptor because there is deliberately no
   * uniform "strip the namespace" rule: a built-in query dispatches on the BARE
   * registered token its `id` does not carry, while an extension query dispatches
   * on its own namespaced `id`. A client that derived the address from `id` would
   * be wrong for one of those two cases and right for the other, silently.
   *
   * So the value is read, never computed. The empty string is the Engine's stated
   * neutral value for a capability the query envelope does not address at all (a
   * plan operation is addressed by `type`, a projection by `view`), and it is
   * never sent as an address — `capabilityTransport.ts` reports that case rather
   * than transmitting it.
   */
  dispatchIntent: string;
  idVersion: number;
  kind: CapabilityKind;
  mode: CapabilityMode;
  permission: string;
  risk: CapabilityRisk;
  review: CapabilityReview;
  transport: string;
  handler: string;
  requestSchema: JsonSchemaFragment;
  resultSchema: JsonSchemaFragment;
  applicability: CapabilityApplicability;
  presentation: CapabilityPresentation;
  referenceExtraction: string;
  compatibility: {
    introducedIn: string;
    changedIn: string;
    genericClientCompatible: boolean;
    /**
     * The Engine's own reachability class for this capability — the field that
     * says WHY a non-generic capability is not generic.
     *
     * Carried rather than derived, and absent-as-undefined rather than defaulted.
     * `genericClientCompatible` is a boolean; on its own it cannot distinguish
     * "specialised, and deliberately not for a generic client" from "internal,
     * and never exposed at all", so a browser holding only the boolean has to
     * either invent that distinction or refuse to report one. Reading the
     * Engine's own class is the only answer that is not this repository's guess,
     * and an Engine that publishes no class yields `undefined` rather than a
     * default — an absent statement must not become a stated one.
     */
    reachability?: string;
    /** The Engine's own prose for the class above. Display evidence, never a rule. */
    reachabilityNote?: string;
  };
  digest: string;
  generatedFrom: string;
}

export interface CapabilityTransport {
  id: string;
  envelope: JsonSchemaFragment;
  generic: boolean;
  summary: string;
}

export interface CapabilityManifest {
  contract: string;
  supportedVersions: readonly string[];
  unsupportedVersionBehaviour: string;
  supportedMajors: readonly number[];
  capabilities: readonly CapabilityDescriptor[];
  projections: readonly CapabilityDescriptor[];
  transports: readonly CapabilityTransport[];
  digest: string;
  generatedFrom: string;
}

// -- validation ---------------------------------------------------------------

const KINDS: ReadonlySet<string> = new Set(["operation", "query", "projection", "extension"]);
const MODES: ReadonlySet<string> = new Set(["READ", "PROPOSE", "EXECUTE"]);
/** Mirrors the Engine's `REVIEW_POLICIES`. See the type for why it is copied. */
const REVIEWS: ReadonlySet<string> = new Set(["none", "change-set", "independent-approval", "human-publication"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function strArray(value: unknown): readonly string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  if (!value.every(entry => typeof entry === "string")) return undefined;
  return Object.freeze([...value] as string[]);
}

/** Identity: a `sha256:` content digest, or absent. Never invented. */
function digestOf(value: unknown): string {
  const text = str(value);
  return text !== undefined && text.startsWith("sha256:") ? text : "";
}

function parseDescriptor(raw: unknown, where: string): CapabilityDescriptor {
  if (!isRecord(raw)) throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} is not an object.`);
  const id = str(raw.id);
  if (id === undefined) throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} has no id.`);
  const kind = str(raw.kind);
  if (kind === undefined || !KINDS.has(kind)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has an unknown kind: ${String(raw.kind)}`);
  }
  const mode = str(raw.mode);
  if (mode === undefined || !MODES.has(mode)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has an unknown mode: ${String(raw.mode)}`);
  }
  const review = str(raw.review);
  if (review === undefined || !REVIEWS.has(review)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has an unknown review policy: ${String(raw.review)}`);
  }
  const permission = str(raw.permission);
  const transport = str(raw.transport);
  const handler = str(raw.handler);
  if (permission === undefined || transport === undefined || handler === undefined) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) is missing permission, transport or handler.`);
  }
  if (!isRecord(raw.applicability)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has no applicability.`);
  }
  const applicability = raw.applicability;
  const requires = strArray(applicability.requires);
  const canonicalInputs = strArray(applicability.canonicalInputs);
  const contextPermission = str(applicability.contextPermission);
  if (requires === undefined || canonicalInputs === undefined || contextPermission === undefined) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has a malformed applicability.`);
  }
  // Presentation is generic metadata, so it is REQUIRED rather than defaulted:
  // a missing group would silently drop the capability out of every UI list.
  const presentationRaw = raw.presentation;
  if (!isRecord(presentationRaw)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has no presentation metadata.`);
  }
  const group = str(presentationRaw.group);
  const summary = str(presentationRaw.summary);
  const primaryFields = strArray(presentationRaw.primaryFields);
  const readOnlyFields = strArray(presentationRaw.readOnlyFields);
  if (group === undefined || summary === undefined || primaryFields === undefined || readOnlyFields === undefined) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has malformed presentation metadata.`);
  }
  if (!isRecord(raw.compatibility)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) has no compatibility data.`);
  }
  // REQUIRED, and required to be a STRING — including the empty string, which is
  // a stated neutral value and not an omission.
  //
  // This is the one field where being lenient would be actively harmful. Without
  // it a browser has no choice but to DERIVE an address from the capability id,
  // and the derivation is the defect the Engine just repaired at source: the id
  // carries a `query/` namespace the route does not dispatch on, so every
  // generic read would be refused with HTTP 400 while the page still rendered. A
  // manifest that does not publish it is refused here instead, at a point where
  // the refusal can name what is missing.
  const dispatchIntent = raw.dispatchIntent;
  if (typeof dispatchIntent !== "string") {
    throw new CapabilityManifestError(
      MANIFEST_MALFORMED,
      `${where} (${id}) publishes no dispatchIntent, so this browser has no published address to send it as.`,
    );
  }
  const compatibility = raw.compatibility;
  if (typeof compatibility.genericClientCompatible !== "boolean") {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, `${where} (${id}) does not declare client compatibility.`);
  }
  return Object.freeze({
    id,
    dispatchIntent,
    idVersion: typeof raw.idVersion === "number" ? raw.idVersion : 1,
    kind: kind as CapabilityKind,
    mode: mode as CapabilityMode,
    permission,
    risk: str(raw.risk) as CapabilityRisk,
    review: review as CapabilityReview,
    transport,
    handler,
    requestSchema: (isRecord(raw.requestSchema) ? raw.requestSchema : Object.freeze({})) as JsonSchemaFragment,
    resultSchema: (isRecord(raw.resultSchema) ? raw.resultSchema : Object.freeze({})) as JsonSchemaFragment,
    applicability: Object.freeze({
      kind: str(applicability.kind) ?? "project",
      contextPermission,
      requires,
      canonicalInputs,
    }),
    presentation: Object.freeze({ group, summary, primaryFields, readOnlyFields }),
    referenceExtraction: str(raw.referenceExtraction) ?? "",
    compatibility: Object.freeze({
      introducedIn: str(compatibility.introducedIn) ?? "",
      changedIn: str(compatibility.changedIn) ?? "",
      genericClientCompatible: compatibility.genericClientCompatible,
      // Carried through verbatim when the Engine states them, ABSENT when it does
      // not. Never defaulted to a class: an unreported reachability must stay
      // unreported, so that "the Engine did not say" stays distinguishable from
      // "the Engine said internal".
      ...(typeof compatibility.reachability === "string"
        ? { reachability: compatibility.reachability }
        : {}),
      ...(typeof compatibility.reachabilityNote === "string"
        ? { reachabilityNote: compatibility.reachabilityNote }
        : {}),
    }),
    digest: digestOf(raw.digest),
    generatedFrom: str(raw.generatedFrom) ?? "",
  });
}

/**
 * Validate a published manifest document. Refuses whole, with a distinct code
 * per failure class, so a UI can tell "upgrade the Engine" from "the contract
 * is broken" from "the bytes changed under us".
 */
export function validateManifest(document: unknown, options: { expectedDigest?: string } = {}): CapabilityManifest {
  if (!isRecord(document)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, "The capability manifest is not an object.");
  }
  const versionRaw = document.manifestVersion;
  if (!isRecord(versionRaw)) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, "The manifest declares no manifestVersion.");
  }
  const contract = str(versionRaw.contract);
  const supported = strArray(versionRaw.supported);
  const majors = Array.isArray(versionRaw.supportedMajors)
    && versionRaw.supportedMajors.every(entry => typeof entry === "number")
    ? Object.freeze([...versionRaw.supportedMajors] as number[])
    : undefined;
  if (contract === undefined || supported === undefined || majors === undefined) {
    throw new CapabilityManifestError(MANIFEST_MALFORMED, "The manifest version block is malformed.");
  }
  // RULE 1 — version, refused rather than substituted.
  if (contract !== SUPPORTED_CONTRACT) {
    throw new CapabilityManifestError(
      MANIFEST_VERSION_UNSUPPORTED,
      `This build serves ${SUPPORTED_CONTRACT}; the Engine published ${contract}.`,
    );
  }
  if (!supported.includes(contract)) {
    throw new CapabilityManifestError(
      MANIFEST_VERSION_UNSUPPORTED,
      `The Engine does not list ${contract} among its own supported versions.`,
    );
  }
  if (!Array.isArray(document.capabilities)) {
    throw new CapabilityManifestError(MANIFEST_INCOMPLETE, "The manifest publishes no capabilities array.");
  }
  if (!Array.isArray(document.projections) || !Array.isArray(document.transports)) {
    throw new CapabilityManifestError(MANIFEST_INCOMPLETE, "The manifest is missing projections or transports.");
  }
  const capabilities = document.capabilities.map((raw, index) => parseDescriptor(raw, `capabilities[${index}]`));
  const projections = document.projections.map((raw, index) => parseDescriptor(raw, `projections[${index}]`));
  // A capability may appear exactly once. A duplicate id would make discovery
  // order-dependent, which is the property a digest is supposed to buy.
  const seen = new Set<string>();
  for (const descriptor of [...capabilities, ...projections]) {
    if (seen.has(descriptor.id)) {
      throw new CapabilityManifestError(MANIFEST_MALFORMED, `The manifest publishes ${descriptor.id} more than once.`);
    }
    seen.add(descriptor.id);
  }
  const transports = document.transports.map((raw, index) => {
    if (!isRecord(raw)) throw new CapabilityManifestError(MANIFEST_MALFORMED, `transports[${index}] is not an object.`);
    const id = str(raw.id);
    if (id === undefined) throw new CapabilityManifestError(MANIFEST_MALFORMED, `transports[${index}] has no id.`);
    return Object.freeze({
      id,
      envelope: (isRecord(raw.envelope) ? raw.envelope : Object.freeze({})) as JsonSchemaFragment,
      generic: raw.generic === true,
      summary: str(raw.summary) ?? "",
    });
  });
  const manifest: CapabilityManifest = Object.freeze({
    contract,
    supportedVersions: supported,
    unsupportedVersionBehaviour: str(versionRaw.unsupportedVersionBehaviour) ?? "refuse",
    supportedMajors: majors,
    capabilities: Object.freeze(capabilities),
    projections: Object.freeze(projections),
    transports: Object.freeze(transports),
    digest: digestOf(document.digest),
    generatedFrom: str(document.generatedFrom) ?? "",
  });
  // RULE 2 — content identity, when the deployment pinned one.
  if (options.expectedDigest !== undefined && manifest.digest !== options.expectedDigest) {
    throw new CapabilityManifestError(
      MANIFEST_DIGEST_MISMATCH,
      `The Engine published ${manifest.digest || "no digest"}; this deployment pins ${options.expectedDigest}.`,
    );
  }
  return manifest;
}

// -- generic accessors --------------------------------------------------------
//
// Everything below reads a descriptor's own published fields. None of them
// enumerates capability ids, so an unseen capability is served by the same
// code path as a known one.

export function allDescriptors(manifest: CapabilityManifest): readonly CapabilityDescriptor[] {
  return Object.freeze([...manifest.capabilities, ...manifest.projections]);
}

export function findCapability(manifest: CapabilityManifest, id: string): CapabilityDescriptor | undefined {
  return allDescriptors(manifest).find(descriptor => descriptor.id === id);
}

/** The generic read/query path. `READ` mode and the query envelope. */
export function queryCapabilities(manifest: CapabilityManifest): readonly CapabilityDescriptor[] {
  return allDescriptors(manifest).filter(d => d.kind === "query");
}

/**
 * The generic mutation path, split by how a human must be involved.
 *
 * `review` is the Engine's own policy and this module never decides it. A
 * `human-approval` capability is one the browser may PREVIEW and STAGE but
 * must never treat as self-approving; the Engine owns that boundary and this
 * list exists so the UI can say so rather than imply otherwise.
 */
export function operationCapabilities(manifest: CapabilityManifest): readonly CapabilityDescriptor[] {
  return allDescriptors(manifest).filter(d => d.kind === "operation" || d.kind === "extension");
}

/**
 * Whether reaching THIS capability requires an independent human.
 *
 * The Engine's own policy decides it and this function only reads it. The two
 * policies that name a person are `independent-approval` and
 * `human-publication`; a capability carrying either may be previewed and
 * staged here but must never be treated as self-approving.
 */
export function requiresHumanReview(descriptor: CapabilityDescriptor): boolean {
  return descriptor.review === "independent-approval" || descriptor.review === "human-publication";
}

/** Capabilities grouped by the Engine's own presentation metadata. */
export function groupedByPresentation(
  manifest: CapabilityManifest,
): ReadonlyMap<string, readonly CapabilityDescriptor[]> {
  const groups = new Map<string, CapabilityDescriptor[]>();
  for (const descriptor of allDescriptors(manifest)) {
    const key = descriptor.presentation.group;
    const list = groups.get(key) ?? [];
    list.push(descriptor);
    groups.set(key, list);
  }
  return new Map([...groups.entries()].map(([k, v]) => [k, Object.freeze(v.sort((a, b) => a.id.localeCompare(b.id)))]));
}

/**
 * The request fields a generic form must offer, in the Engine's own order.
 *
 * `required` first, then the rest alphabetically, so the rendered form is
 * deterministic for a given manifest digest. `readOnlyFields` are returned
 * separately because the Engine, not the browser, decides what is writable.
 */
export function configurableFields(descriptor: CapabilityDescriptor): {
  readonly required: readonly string[];
  readonly optional: readonly string[];
  readonly readOnly: readonly string[];
} {
  const properties = Object.keys(descriptor.requestSchema.properties ?? {}).sort();
  const required = new Set(descriptor.requestSchema.required ?? []);
  const readOnly = new Set(descriptor.presentation.readOnlyFields);
  return Object.freeze({
    required: Object.freeze(properties.filter(name => required.has(name))),
    optional: Object.freeze(properties.filter(name => !required.has(name) && !readOnly.has(name))),
    readOnly: Object.freeze([...readOnly].sort()),
  });
}

// -- retrieval ----------------------------------------------------------------

export interface CapabilityFetchOptions {
  /** Pins manifest CONTENT. Distinct from the version, which pins shape. */
  expectedDigest?: string;
  fetchImpl?: typeof fetch;
  baseUrl?: string;
  signal?: AbortSignal;
}

/**
 * Fetch and validate the Engine's published manifest.
 *
 * The Engine is asked for the version this build serves, so an incompatible
 * pair is refused by negotiation before it is refused by inspection.
 */
export async function fetchCapabilityManifest(
  options: CapabilityFetchOptions = {},
): Promise<CapabilityManifest> {
  const base = (options.baseUrl ?? "").replace(/\/+$/, "");
  const doFetch = options.fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const url = `${base}/api/v1/capabilities?version=${encodeURIComponent(SUPPORTED_CONTRACT)}`;
  let response: Response;
  try {
    response = await doFetch(url, {
      method: "GET",
      headers: { accept: "application/json" },
      credentials: "same-origin",
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    throw new TransportError("The capability manifest could not be retrieved.", cause);
  }
  if (response.status === 400) {
    // The Engine refused the version by negotiation. Report the negotiation
    // failure itself rather than a shape failure, because the remedy differs.
    throw new CapabilityManifestError(
      MANIFEST_VERSION_UNSUPPORTED,
      `The Engine refused to negotiate ${SUPPORTED_CONTRACT}.`,
      400,
    );
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let body: unknown;
    try { body = text ? JSON.parse(text) : undefined; } catch { body = undefined; }
    if (isRecord(body) && typeof body.code === "string" && typeof body.message === "string") {
      throw new ApiError(response.status, {
        code: body.code,
        message: body.message,
        ...(isRecord(body.details) ? { details: body.details } : {}),
        ...(typeof body.requestId === "string" ? { requestId: body.requestId } : {}),
      } as never);
    }
    throw new TransportError(`The capability manifest request answered ${response.status}.`);
  }
  const text = await response.text();
  let document: unknown;
  try { document = text ? JSON.parse(text) : undefined; }
  catch { throw new CapabilityManifestError(MANIFEST_MALFORMED, "The capability manifest is not JSON."); }
  return validateManifest(document, options.expectedDigest !== undefined ? { expectedDigest: options.expectedDigest } : {});
}

export { ApiError, TransportError };
